import React, { useEffect, useState } from 'react';
import { X, Camera, Plus, Trash2, Check } from 'lucide-react';
interface EditPropertyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (property: any) => void;
  property: any;
}
export function EditPropertyModal({
  isOpen,
  onClose,
  onSave,
  property
}: EditPropertyModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: 'Roma',
    zone: '',
    sqm: '',
    rooms: '',
    bathrooms: '',
    issues: ''
  });
  const [checklist, setChecklist] = useState<string[]>([]);
  useEffect(() => {
    if (property) {
      setFormData({
        name: property.name,
        address: property.address,
        city: property.city || 'Roma',
        zone: property.zone.replace('Roma, ', ''),
        sqm: property.sqm.toString(),
        rooms: property.rooms.toString(),
        bathrooms: property.bathrooms.toString(),
        issues: property.issues.join('\n')
      });
      setChecklist(property.checklist || []);
    }
  }, [property]);
  if (!isOpen || !property) return null;
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      ...property,
      ...formData,
      sqm: Number(formData.sqm),
      rooms: Number(formData.rooms),
      bathrooms: Number(formData.bathrooms),
      checklist,
      issues: formData.issues ? formData.issues.split('\n').filter(Boolean) : [],
      zone: `Roma, ${formData.zone}` // Reconstruct zone format
    });
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6" data-id="element-908">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity" onClick={onClose} data-id="element-909" />

      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col animate-in fade-in zoom-in-95 duration-200" data-id="element-910">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100" data-id="element-911">
          <h2 className="text-xl font-bold font-montserrat text-navy" data-id="element-912">
            Modifica Proprietà
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full text-gray-400 hover:text-navy transition-colors" data-id="element-913">
            <X size={20} data-id="element-914" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6" data-id="element-915">
          <form id="edit-property-form" onSubmit={handleSubmit} className="space-y-6" data-id="element-916">
            {/* Basic Info */}
            <div className="space-y-4" data-id="element-917">
              <h3 className="text-sm font-semibold text-navy uppercase tracking-wider" data-id="element-918">
                Dettagli Principali
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-919">
                <div className="md:col-span-2" data-id="element-920">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-921">
                    Nome Proprietà
                  </label>
                  <input type="text" required className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-deep focus:border-transparent outline-none" value={formData.name} onChange={e => setFormData({
                  ...formData,
                  name: e.target.value
                })} data-id="element-922" />
                </div>

                <div className="md:col-span-2" data-id="element-923">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-924">
                    Indirizzo
                  </label>
                  <input type="text" required className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-deep focus:border-transparent outline-none" value={formData.address} onChange={e => setFormData({
                  ...formData,
                  address: e.target.value
                })} data-id="element-925" />
                </div>

                <div data-id="element-926">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-927">
                    Città
                  </label>
                  <input type="text" required className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-deep focus:border-transparent outline-none" value={formData.city} onChange={e => setFormData({
                  ...formData,
                  city: e.target.value
                })} data-id="element-928" />
                </div>

                <div data-id="element-929">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-930">
                    Zona
                  </label>
                  <input type="text" required className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-deep focus:border-transparent outline-none" value={formData.zone} onChange={e => setFormData({
                  ...formData,
                  zone: e.target.value
                })} data-id="element-931" />
                </div>
              </div>
            </div>

            {/* Specs */}
            <div className="space-y-4" data-id="element-932">
              <h3 className="text-sm font-semibold text-navy uppercase tracking-wider" data-id="element-933">
                Caratteristiche
              </h3>
              <div className="grid grid-cols-3 gap-4" data-id="element-934">
                <div data-id="element-935">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-936">
                    Mq
                  </label>
                  <input type="number" required className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-deep focus:border-transparent outline-none" value={formData.sqm} onChange={e => setFormData({
                  ...formData,
                  sqm: e.target.value
                })} data-id="element-937" />
                </div>
                <div data-id="element-938">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-939">
                    Camere
                  </label>
                  <input type="number" required className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-deep focus:border-transparent outline-none" value={formData.rooms} onChange={e => setFormData({
                  ...formData,
                  rooms: e.target.value
                })} data-id="element-940" />
                </div>
                <div data-id="element-941">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-942">
                    Bagni
                  </label>
                  <input type="number" required className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-deep focus:border-transparent outline-none" value={formData.bathrooms} onChange={e => setFormData({
                  ...formData,
                  bathrooms: e.target.value
                })} data-id="element-943" />
                </div>
              </div>
            </div>

            {/* Photos */}
            <div className="space-y-4" data-id="element-944">
              <h3 className="text-sm font-semibold text-navy uppercase tracking-wider" data-id="element-945">
                Galleria Fotografica
              </h3>
              <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 flex flex-col items-center justify-center text-center hover:bg-gray-50 transition-colors cursor-pointer group" data-id="element-946">
                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3 group-hover:bg-white group-hover:shadow-sm transition-all" data-id="element-947">
                  <Camera className="text-gray-400 group-hover:text-teal-deep" size={24} data-id="element-948" />
                </div>
                <p className="text-sm font-medium text-navy" data-id="element-949">
                  Clicca per caricare foto
                </p>
                <p className="text-xs text-gray-500 mt-1" data-id="element-950">
                  JPG, PNG fino a 5MB
                </p>
              </div>
            </div>

            {/* Checklist */}
            <div className="space-y-4" data-id="element-951">
              <div className="flex items-center justify-between" data-id="element-952">
                <h3 className="text-sm font-semibold text-navy uppercase tracking-wider" data-id="element-953">
                  Checklist Pulizia Ordinaria
                </h3>
                <button type="button" className="text-xs font-medium text-teal-deep hover:text-teal-700 flex items-center gap-1" data-id="element-954">
                  <Plus size={14} data-id="element-955" /> Aggiungi attività
                </button>
              </div>
              <div className="space-y-2" data-id="element-956">
                {checklist.map((item, idx) => <div key={idx} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100" data-id="element-957">
                    <div className="w-5 h-5 rounded bg-teal-deep flex items-center justify-center flex-shrink-0" data-id="element-958">
                      <Check size={12} className="text-white" data-id="element-959" />
                    </div>
                    <span className="text-sm text-gray-700 flex-1" data-id="element-960">{item}</span>
                    <button type="button" className="text-gray-400 hover:text-red-500 transition-colors" data-id="element-961">
                      <Trash2 size={16} data-id="element-962" />
                    </button>
                  </div>)}
              </div>
            </div>

            {/* Issues */}
            <div className="space-y-4" data-id="element-963">
              <h3 className="text-sm font-semibold text-navy uppercase tracking-wider" data-id="element-964">
                Problemi Noti
              </h3>
              <textarea className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-deep focus:border-transparent outline-none resize-none h-24" value={formData.issues} onChange={e => setFormData({
              ...formData,
              issues: e.target.value
            })} data-id="element-965" />
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-100 bg-gray-50 rounded-b-2xl flex justify-end gap-3" data-id="element-966">
          <button onClick={onClose} className="px-4 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-white hover:text-navy transition-colors" data-id="element-967">
            Annulla
          </button>
          <button type="submit" form="edit-property-form" className="px-6 py-2.5 bg-teal-deep text-white rounded-lg text-sm font-medium hover:bg-teal-deep/90 transition-colors shadow-sm" data-id="element-968">
            Salva Modifiche
          </button>
        </div>
      </div>
    </div>;
}